import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MapPin, CheckCircle2 } from 'lucide-react';
import IconHeartOutline from '@/assets/icon_heart_outline.svg';
import IconHeartFilled from '@/assets/icon_heart_filled.svg';
import IconMessage from '@/assets/icon_message.svg';
import IconBookmark from '@/assets/icon_bookmark.svg';
import IconBookmarkFilled from '@/assets/icon_bookmark_filled.svg';
import IconSquareList from '@/assets/icon_square_list.svg';
import IconUpload from '@/assets/icon_upload.svg';
import type { Ticket, Project } from '@mangelapp/shared';
import { apiRequest } from '@/lib/queryClient';
import { getStatusColor } from '@/utils/color-utils';
import { getInitials } from '@/lib/utils';
import { usePriorities } from '@/hooks/use-priorities';
import { useCurrentOrganization } from '@/hooks/use-current-organization';
import { useDateFormat } from '@/utils/date-formatter';
import { cn } from '@/lib/utils';
import { ConicGradientBorder } from '@/components/conic-gradient-border';
import LogoOutlineSvg from '@/assets/icon_logo_folw_outline.svg';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import TicketChat from '@/components/ticket-chat';
import EditTicketInlineModal from '@/components/edit-ticket-inline-modal';
import { TicketExportOptionsModal } from '@/pages/project-details/components/TicketExportOptionsModal';
import { TicketPDFExportService, type TicketExportData, type ExportOptions } from '@/pages/project-details/services/TicketPDFExportService';
import { useToast } from '@/hooks/use-toast';
import { LogoLoader } from '@/components/ui/logo-loader';
import { apiClient } from '@/lib/api-client';

interface MiniTicketCardProps {
  ticket: Ticket;
  project?: Project;
  organizationId?: string;
  className?: string;
  isPublicView?: boolean;
  onOpenTicket?: () => void;
  onTicketUpdated?: () => void;
}

interface StatusData {
  id: string;
  name: string;
  displayName?: string;
  color: string;
  userId: string;
  createdAt: string;
}

// Placeholder component for missing ticket image - white with logo
function TicketImagePlaceholder() {
  return (
    <div className="w-full h-[180px] bg-white flex items-center justify-center rounded-t-[32px]">
      <img src={LogoOutlineSvg} alt="Logo" className="w-20 h-20 opacity-50" />
    </div>
  );
}

// Placeholder component for missing plan image - white with logo
function PlanImagePlaceholder() {
  return (
    <div className="w-full h-32 bg-white flex items-center justify-center">
      <img src={LogoOutlineSvg} alt="Logo" className="w-16 h-16 opacity-50" />
    </div>
  );
}

// User avatar component for ticket creator with ConicGradientBorder
function CreatorAvatar({ userId, reporterName, reporterProfilePhoto, organizationId, isPublicView = false }: { userId: string; reporterName?: string; reporterProfilePhoto?: string | null; organizationId?: string; isPublicView?: boolean }) {
  // Fetch contractors (disabled for public view - use props instead)
  const { data: contractorsResponse } = useQuery({
    queryKey: ['/api/contractors', organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/contractors?organizationId=${encodeURIComponent(organizationId)}` : '/api/contractors';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !isPublicView,
  });

  // Fetch organization members (internal users) - disabled for public view
  const { data: orgMembersResponse } = useQuery({
    queryKey: ['/api/orgs', organizationId, 'members'],
    queryFn: async () => {
      const response = await apiRequest(`/api/orgs/${organizationId}/members`);
      return await response.json();
    },
    enabled: !isPublicView && !!organizationId,
  });

  // Combine both contractors and organization members
  const allMembers = useMemo(() => {
    const members: any[] = [];
    
    // Add contractors
    if (contractorsResponse?.success && Array.isArray(contractorsResponse.data)) {
      members.push(...contractorsResponse.data);
    } else if (Array.isArray(contractorsResponse)) {
      members.push(...contractorsResponse);
    }
    
    // Add organization members
    if (orgMembersResponse?.success && Array.isArray(orgMembersResponse.data)) {
      members.push(...orgMembersResponse.data);
    } else if (Array.isArray(orgMembersResponse)) {
      members.push(...orgMembersResponse);
    }
    
    return members;
  }, [contractorsResponse, orgMembersResponse]);

  const userFromMembers = useMemo(() => {
    return allMembers.find((m: any) => 
      m.user_id === userId || 
      m.userId === userId || 
      m.id === userId
    );
  }, [allMembers, userId]);

  const displayName = userFromMembers?.display_name || userFromMembers?.displayName || userFromMembers?.name || 
    (userFromMembers?.firstName && userFromMembers?.lastName ? `${userFromMembers.firstName} ${userFromMembers.lastName}` : null) ||
    (userFromMembers?.first_name && userFromMembers?.last_name ? `${userFromMembers.first_name} ${userFromMembers.last_name}` : null) ||
    reporterName || 'U';
  const initials = getInitials(displayName);
  
  // For public view, use the profile photo from ticket data directly
  // Otherwise, fetch from member lists
  const profilePhotoUrl = isPublicView 
    ? reporterProfilePhoto 
    : (userFromMembers?.profile_photo_url || userFromMembers?.profilePhotoUrl);

  return (
    <ConicGradientBorder size={58} borderWidth={4}>
      {profilePhotoUrl ? (
        <img
          src={profilePhotoUrl}
          alt={displayName}
          className="w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.className = 'w-full h-full bg-[#4A90E2] flex items-center justify-center';
              parent.innerHTML = `<span class="text-white font-medium text-sm">${initials}</span>`;
            }
          }}
        />
      ) : (
        <div className="w-full h-full bg-[#4A90E2] flex items-center justify-center">
          <span className="text-white font-medium text-sm">{initials}</span>
        </div>
      )}
    </ConicGradientBorder>
  );
}

// LabeledContainer-style field component
function LabeledField({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('relative mt-3', className)}>
      <div className="relative rounded-lg px-3 py-2.5 bg-[#EBEBEB]">
        <span className="absolute -top-2.5 left-3 bg-[#EBEBEB] px-1 text-xs font-medium text-slate-700">
          {label}
        </span>
        {children}
      </div>
    </div>
  );
}

export function MiniTicketCard({ ticket, project, organizationId: propOrganizationId, className, isPublicView = false, onOpenTicket, onTicketUpdated }: MiniTicketCardProps) {
  const { t } = useTranslation(['components.ticket', 'pages.calendar', 'pages.projectDetails']);
  const { dateFormat, timeZone, formatDate } = useDateFormat();
  const { organizationId: currentOrgId } = useCurrentOrganization();
  const organizationId = propOrganizationId || currentOrgId;
  const { data: priorities = [] } = usePriorities({ enabled: !isPublicView });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Modal states
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Like state
  const [isLikeInProgress, setIsLikeInProgress] = useState(false);
  const [localLikeCount, setLocalLikeCount] = useState<number | undefined>(undefined);
  const [userHasLiked, setUserHasLiked] = useState(false);

  // Bookmark state
  const [isBookmarkInProgress, setIsBookmarkInProgress] = useState(false);

  // Query for user like status
  const { data: userLikeStatus } = useQuery({
    queryKey: [`/api/tickets/${ticket.id}/user-liked`],
    queryFn: async () => {
      const response = await apiRequest(`/api/tickets/${ticket.id}/user-liked`);
      return await response.json();
    },
    enabled: !!ticket.id && !isPublicView,
    staleTime: 30000,
  });

  // Update userHasLiked when query data changes
  useEffect(() => {
    if (userLikeStatus?.success && userLikeStatus.data) {
      setUserHasLiked(userLikeStatus.data.hasLiked || false);
    }
  }, [userLikeStatus]);

  // Query for bookmarks
  const { data: bookmarksResponse } = useQuery({
    queryKey: ['/api/saved-photos', organizationId],
    queryFn: async () => {
      const url = organizationId 
        ? `/api/saved-photos?organizationId=${encodeURIComponent(organizationId)}` 
        : '/api/saved-photos';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !isPublicView,
  });

  // Check if ticket is bookmarked
  const bookmarkData = useMemo(() => {
    if (!bookmarksResponse?.success || !Array.isArray(bookmarksResponse.data)) {
      return null;
    }
    return bookmarksResponse.data.find((bookmark: any) => 
      bookmark.metadata?.type === 'ticket_bookmark' && 
      bookmark.metadata?.ticketId === ticket.id
    );
  }, [bookmarksResponse, ticket.id]);

  const isBookmarked = !!bookmarkData;

  // Like handler - optimistic update
  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (isLikeInProgress || isPublicView) return;
    
    setIsLikeInProgress(true);
    const previousLikedState = userHasLiked;
    const previousLikeCount = localLikeCount !== undefined ? localLikeCount : (ticket.likes || 0);

    // Optimistic update - update UI immediately
    setUserHasLiked(!previousLikedState);
    setLocalLikeCount(previousLikedState ? Math.max(0, previousLikeCount - 1) : previousLikeCount + 1);

    try {
      const endpoint = previousLikedState
        ? `/api/tickets/${ticket.id}/unlike`
        : `/api/tickets/${ticket.id}/like`;
      
      const response = await apiRequest(endpoint, { method: 'POST' });
      const result = await response.json();
      
      if (result.success && result.data) {
        // Update with server response (in case count differs)
        const serverLikeCount = result.data.likes ?? 0;
        setLocalLikeCount(serverLikeCount);
        
        // Update query cache
        queryClient.setQueryData([`/api/tickets/${ticket.id}/user-liked`], {
          success: true,
          data: { hasLiked: !previousLikedState }
        });
        
        // Invalidate ticket queries in background
        queryClient.invalidateQueries({ queryKey: ['/api/tickets'] });
      } else {
        // Rollback on failure
        setUserHasLiked(previousLikedState);
        setLocalLikeCount(previousLikeCount);
      }
    } catch (error) {
      // Rollback on error
      setUserHasLiked(previousLikedState);
      setLocalLikeCount(previousLikeCount);
      console.error('Like operation failed:', error);
      toast({
        title: t('components.ticket:errors.likeError'),
        variant: 'destructive',
      });
    } finally {
      setIsLikeInProgress(false);
    }
  };

  // Bookmark handler - optimistic update
  const handleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (isBookmarkInProgress || isPublicView) return;
    
    setIsBookmarkInProgress(true);
    
    // Store previous state for rollback
    const wasBookmarked = isBookmarked;
    const previousBookmarkData = bookmarkData;
    const queryKey = ['/api/saved-photos', organizationId];
    const previousQueryData = queryClient.getQueryData(queryKey);

    // Optimistic update - update cache immediately
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData?.data) return oldData;
      
      if (wasBookmarked && previousBookmarkData) {
        // Remove bookmark from cache
        return {
          ...oldData,
          data: oldData.data.filter((b: any) => b.id !== previousBookmarkData.id)
        };
      } else {
        // Add temporary bookmark to cache
        const tempBookmark = {
          id: `temp-${ticket.id}`,
          metadata: {
            type: 'ticket_bookmark',
            ticketId: ticket.id,
            imageIndex: 0,
          },
        };
        return {
          ...oldData,
          data: [...oldData.data, tempBookmark]
        };
      }
    });

    try {
      if (wasBookmarked && previousBookmarkData) {
        // Unbookmark - delete the saved photo
        const response = await apiRequest(`/api/saved-photos/${previousBookmarkData.id}`, {
          method: 'DELETE',
        });
        const result = await response.json();
        
        if (!result.success) {
          // Rollback on failure
          queryClient.setQueryData(queryKey, previousQueryData);
        } else {
          // Refresh to get accurate data
          queryClient.invalidateQueries({ queryKey: ['/api/saved-photos'] });
        }
      } else {
        // Bookmark - save new
        const ticketImage = getTicketImageUrl();
        const response = await apiRequest('/api/saved-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: ticketImage || '',
            organizationId,
            metadata: {
              type: 'ticket_bookmark',
              ticketId: ticket.id,
              imageIndex: 0,
            },
          }),
        });
        const result = await response.json();
        
        if (!result.success) {
          // Rollback on failure
          queryClient.setQueryData(queryKey, previousQueryData);
        } else {
          // Refresh to get accurate data with real ID
          queryClient.invalidateQueries({ queryKey: ['/api/saved-photos'] });
        }
      }
    } catch (error) {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousQueryData);
      console.error('Bookmark operation failed:', error);
    } finally {
      setIsBookmarkInProgress(false);
    }
  };

  // Get ticket image URL helper
  const getTicketImageUrl = () => {
    const images = (ticket as any).images || [];
    if (images.length > 0 && typeof images[0] === 'string') {
      const img = images[0];
      if (img.startsWith('data:') || img.startsWith('http://') || img.startsWith('https://')) {
        return img;
      }
      return img.startsWith('/objects/') ? img : `/objects${img}`;
    }
    const image1Url = (ticket as any).image1Url;
    if (image1Url) {
      if (image1Url.startsWith('data:') || image1Url.startsWith('http://') || image1Url.startsWith('https://')) {
        return image1Url;
      }
      return image1Url.startsWith('/objects/') ? image1Url : `/objects${image1Url}`;
    }
    return null;
  };

  // Export PDF handler
  const handleExportPDF = async (options: ExportOptions) => {
    setIsExporting(true);
    setIsExportModalOpen(false);

    try {
      // Collect ticket data for export
      const ticketExportData: TicketExportData = {
        id: ticket.id,
        ticketNumber: ticket.ticketNumber || '',
        title: ticket.title,
        status: (ticket as any).status || '',
        prioritaet: ticket.priority || '',
        zugewiesenePerson: ticket.assigneeName || undefined,
        standort: ticket.location || undefined,
        beschreibung: ticket.description || undefined,
        gewerk: ticket.gewerk || undefined,
        typ: ticket.type || undefined,
        faelligkeitsdatum: ticket.dueDate ? formatDate(ticket.dueDate) : undefined,
        geschaetzteKosten: ticket.costs || undefined,
        fortschritt: ticket.progress || undefined,
        planLocation: (ticket as any).planLocation || undefined,
        photos: options.includePhotos ? ((ticket as any).images || []) : undefined,
      };

      // Fetch history if needed
      if (options.includeHistory) {
        try {
          const historyResponse = await apiClient.getTicketHistory(ticket.id, {
            limit: 200,
            offset: 0,
          });
          if (Array.isArray(historyResponse.items) && historyResponse.items.length > 0) {
            ticketExportData.history = historyResponse.items;
          }
        } catch (e) {
          console.warn('Could not fetch ticket history:', e);
        }
      }

      // Fetch messages if needed
      if (options.includeMessages) {
        try {
          const chatResponse = await apiClient.getTicketChat(ticket.id);
          if (chatResponse?.messages) {
            ticketExportData.messages = chatResponse.messages;
          }
        } catch (e) {
          console.warn('Could not fetch ticket chat:', e);
        }
      }

      const exporter = new TicketPDFExportService({
        dateFormat,
        timeZone,
      });
      exporter.exportTickets([ticketExportData], project?.name || 'Project', options);

      toast({
        title: t('components.ticket:toasts.pdfCreated'),
        description: t('components.ticket:toasts.pdfCreatedDesc'),
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: t('components.ticket:toasts.errorTitle'),
        description: t('components.ticket:toasts.pdfFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Handle ticket modal close
  const handleTicketModalClose = () => {
    setIsTicketModalOpen(false);
    onTicketUpdated?.();
    queryClient.invalidateQueries({ queryKey: ['/api/tickets'] });
  };

  // Fetch status data for border color
  const { data: statusResponse } = useQuery({
    queryKey: ['/api/status', organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : '/api/status';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId && !isPublicView,
  });

  const statusData: StatusData[] = useMemo(() => {
    if (!statusResponse) return [];
    if (statusResponse.success && Array.isArray(statusResponse.data)) {
      return statusResponse.data;
    }
    if (Array.isArray(statusResponse)) return statusResponse;
    return [];
  }, [statusResponse]);

  const statusColor = useMemo(() => {
    const ticketStatusId = (ticket as any).statusId as string | undefined;
    const ticketStatus = (ticket as any).status as string | undefined;
    const ticketStatusColor = (ticket as any).statusColor as string | undefined;

    // For public view, use the pre-resolved color from the API
    if (isPublicView && ticketStatusColor) {
      return ticketStatusColor;
    }

    // Resolve by status ID first to stay aligned with calendar and new status model.
    if (ticketStatusId) {
      const statusById = statusData.find((s) => s.id === ticketStatusId);
      if (statusById?.color) {
        return statusById.color;
      }
    }

    // Fallback to name/displayName matching.
    if (ticketStatus) {
      const normalizedStatus = ticketStatus.toLowerCase().trim();
      const statusByName = statusData.find((s: any) => {
        const name = (s.name || '').toLowerCase().trim();
        const displayName = (s.displayName || '').toLowerCase().trim();
        return name === normalizedStatus || displayName === normalizedStatus;
      });
      if (statusByName?.color) {
        return statusByName.color;
      }
    }

    return getStatusColor(ticketStatus || '', statusData);
  }, [(ticket as any).statusId, (ticket as any).status, (ticket as any).statusColor, statusData, isPublicView]);

  // Fetch comments count
  const { data: commentsResponse } = useQuery({
    queryKey: ['/api/tickets', ticket.id, 'chat'],
    queryFn: async () => {
      const response = await apiRequest(`/api/tickets/${ticket.id}/chat`);
      return await response.json();
    },
    enabled: !!ticket.id && !isPublicView,
  });

  const commentsCount = useMemo(() => {
    if (!commentsResponse) return 0;
    if (Array.isArray(commentsResponse)) return commentsResponse.length;
    if (commentsResponse.success && Array.isArray(commentsResponse.data)) {
      return commentsResponse.data.length;
    }
    return 0;
  }, [commentsResponse]);

  // Get ticket image
  const ticketImage = useMemo(() => {
    const images = (ticket as any).images || [];
    if (images.length > 0 && typeof images[0] === 'string') {
      const img = images[0];
      if (img.startsWith('data:') || img.startsWith('http://') || img.startsWith('https://')) {
        return img;
      }
      return img.startsWith('/objects/') ? img : `/objects${img}`;
    }
    const image1Url = (ticket as any).image1Url;
    if (image1Url) {
      if (image1Url.startsWith('data:') || image1Url.startsWith('http://') || image1Url.startsWith('https://')) {
        return image1Url;
      }
      return image1Url.startsWith('/objects/') ? image1Url : `/objects${image1Url}`;
    }
    return null;
  }, [ticket]);

  const [ticketImageError, setTicketImageError] = useState(false);

  useEffect(() => {
    setTicketImageError(false);
  }, [ticket.id, ticketImage]);

  // Get plan preview image
  const planPreviewImage = (ticket as any).planPreviewImage;
  const planPinPosition = (ticket as any).planPreviewPinPosition;
  const hasImage = ticketImage && !ticketImageError;

  // Fetch gewerke for display validation
  const { data: gewerkeResponse } = useQuery({
    queryKey: ['/api/gewerke', organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` : '/api/gewerke';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId && !isPublicView,
  });

  const gewerke = useMemo(() => {
    if (!gewerkeResponse) return [];
    if (gewerkeResponse.success && Array.isArray(gewerkeResponse.data)) {
      return gewerkeResponse.data;
    }
    if (Array.isArray(gewerkeResponse)) return gewerkeResponse;
    return [];
  }, [gewerkeResponse]);

  // Fetch typen for display validation
  const { data: typenResponse } = useQuery({
    queryKey: ['/api/typen', organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/typen?organizationId=${encodeURIComponent(organizationId)}` : '/api/typen';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId && !isPublicView,
  });

  const typen = useMemo(() => {
    if (!typenResponse) return [];
    if (typenResponse.success && Array.isArray(typenResponse.data)) {
      return typenResponse.data;
    }
    if (Array.isArray(typenResponse)) return typenResponse;
    return [];
  }, [typenResponse]);

  // Fetch team members for assigned name
  const { data: teamMembersResponse } = useQuery({
    queryKey: ['/api/contractors'],
    queryFn: async () => {
      const response = await apiRequest('/api/contractors');
      return await response.json();
    },
    enabled: !isPublicView,
  });

  const teamMembers = useMemo(() => {
    if (!teamMembersResponse) return [];
    if (teamMembersResponse.success && Array.isArray(teamMembersResponse.data)) {
      return teamMembersResponse.data;
    }
    if (Array.isArray(teamMembersResponse)) return teamMembersResponse;
    return [];
  }, [teamMembersResponse]);

  // Get priority info
  const priorityInfo = useMemo(() => {
    const priorityId = (ticket as any).priorityId;
    const priorityText = ticket.priority;
    const priorityColor = (ticket as any).priorityColor;

    // For public view, use the pre-resolved data from the API
    if (isPublicView) {
      return {
        name: priorityText || '-',
        color: priorityColor || '#6b7280',
      };
    }

    if (priorityId && priorities.length > 0) {
      const priority = priorities.find(p => p.id === priorityId);
      if (priority) {
        return {
          name: priority.displayName || priority.name,
          color: priority.color || '#6b7280',
        };
      }
    }

    if (priorityText) {
      if (priorities.length > 0) {
        const priority = priorities.find(p => p.name.toLowerCase() === priorityText.toLowerCase());
        if (priority) {
          return {
            name: priority.displayName || priority.name,
            color: priority.color || '#6b7280',
          };
        }
      }
      return {
        name: priorityText,
        color: '#6b7280',
      };
    }

    return {
      name: '-',
      color: '#6b7280',
    };
  }, [ticket, priorities, isPublicView]);

  // Get status display name (use displayName from API when available, same as main ticket card / filters)
  const statusDisplayName = useMemo(() => {
    if (!(ticket as any).status) return '-';
    
    // For public view, the status is already the translated display name from the API
    if (isPublicView) {
      return (ticket as any).status;
    }
    
    const statusItem = statusData.find(
      (s: any) => s.name === (ticket as any).status || s.id === (ticket as any).statusId
    );
    return statusItem?.displayName || statusItem?.name || (ticket as any).status;
  }, [(ticket as any).status, (ticket as any).statusId, statusData, isPublicView]);

  // Get assigned person name
  const assignedName = useMemo(() => {
    const assignedContractorId = (ticket as any).assignedContractorId;
    if (assignedContractorId && teamMembers.length > 0) {
      const contractor = teamMembers.find((m: any) => m.user_id === assignedContractorId || m.id === assignedContractorId);
      if (contractor) {
        return contractor.display_name || contractor.name || contractor.email || '-';
      }
    }
    if (ticket.assigneeName) {
      const member = teamMembers.find((m: any) => m.name === ticket.assigneeName);
      if (member && member.lastName) {
        return `${member.name} ${member.lastName}`;
      }
      return ticket.assigneeName;
    }
    return t('pages.calendar:notAssigned');
  }, [ticket, teamMembers, t]);

  // Get overtime info
  const overtimeNeeded = (ticket as any).overtimeNeeded;
  const overtimeHours = (ticket as any).overtimeHours;

  // Get gewerk display name (use displayName from API when available, same as list items)
  const gewerkDisplayName = useMemo(() => {
    // For public view, gewerk is already the translated display name from the API
    if (isPublicView) {
      return ticket.gewerk || '-';
    }
    
    const gewerkId = (ticket as any).gewerkId;
    if (gewerkId && gewerke.length > 0) {
      const gewerkItem = gewerke.find((g: any) => g.id === gewerkId);
      if (gewerkItem) {
        return gewerkItem.displayName || gewerkItem.name;
      }
    }
    return ticket.gewerk || '-';
  }, [ticket, gewerke, isPublicView]);

  // Get type display name (use displayName from API when available, same as list items)
  const typeDisplayName = useMemo(() => {
    // For public view, type is already the translated display name from the API
    if (isPublicView) {
      return ticket.type || '-';
    }
    
    const typeId = (ticket as any).typeId;
    if (typeId && typen.length > 0) {
      const typeItem = typen.find((ty: any) => ty.id === typeId);
      if (typeItem) {
        return typeItem.displayName || typeItem.name;
      }
    }
    return ticket.type || '-';
  }, [ticket, typen, isPublicView]);

  return (
    <div
      className={cn(
        'w-full max-w-[375px] bg-white rounded-[16px] overflow-hidden relative',
        className
      )}
    >
      {/* Full-height left status bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-3 z-10"
        style={{ backgroundColor: statusColor }}
      />

      {/* Header Section - Top Image */}
      <div className="relative">
        {/* Main Ticket Image - 160px height */}
        {hasImage ? (
          <img
            src={ticketImage}
            alt={ticket.title}
            className="w-full h-[160px] object-cover rounded-t-[16px]"
            onError={() => setTicketImageError(true)}
          />
        ) : (
          <TicketImagePlaceholder />
        )}

        {/* Creator Avatar - Right Center of image */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
          <CreatorAvatar
            userId={ticket.userId}
            reporterName={ticket.reporterName}
            reporterProfilePhoto={(ticket as any).reporterProfilePhoto}
            organizationId={organizationId}
            isPublicView={isPublicView}
          />
        </div>

        {/* Project Name Overlay - Semi-transparent, left-aligned */}
        {(project?.name || (ticket as any).projectName) && (
          <div className="absolute bottom-2 left-16 right-16">
            <div className={cn(
              'rounded-full px-4 py-2 text-center',
              hasImage ? 'bg-white/60' : 'bg-black/60'
            )}>
              <span className={cn(
                'text-sm font-semibold truncate block',
                hasImage ? 'text-gray-900' : 'text-white'
              )}>
                {project?.name || (ticket as any).projectName}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Floor Plan Section with ID Badge */}
      <div className="relative bg-gray-50">
        {planPreviewImage ? (
          <div className="relative">
            <img
              src={planPreviewImage}
              alt="Plan preview"
              className="w-full h-32 object-cover bg-white"
            />
            {planPinPosition && (
              <div
                className="absolute"
                style={{
                  left: `${planPinPosition.x}%`,
                  top: `${planPinPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <MapPin className="h-6 w-6 text-red-500 fill-red-500" />
              </div>
            )}
          </div>
        ) : (
          <PlanImagePlaceholder />
        )}

        {/* ID Badge - overlapping plan section, left side */}
        <div className="absolute top-2 left-5 z-10">
          <div className="bg-gray-200/90 rounded-lg px-3 py-1.5">
            <span className="text-xs font-bold text-black">ID No. {ticket.ticketNumber}</span>
          </div>
        </div>
      </div>

      {/* Ticket Details Form Section */}
      <div className="flex bg-[#EBEBEB]">
        {/* Main Content Area */}
        <div className="flex-1 pl-5 pr-2 py-3">
          {/* Titel */}
          <LabeledField label={t('components.ticket:fields.title')} className="mt-0">
            <span className="text-xs font-normal text-slate-900">{ticket.title}</span>
          </LabeledField>

          {/* Divider */}
          <div className="h-px bg-black my-3" />

          {/* Beschreibung */}
          <LabeledField label={t('components.ticket:fields.description')}>
            <span className="text-xs font-normal text-slate-900 whitespace-pre-wrap line-clamp-3">
              {ticket.description}
            </span>
          </LabeledField>

          {/* Divider */}
          <div className="h-px bg-black my-3" />

          {/* Priorität und Status - Side by Side */}
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label={t('components.ticket:fields.priority')}>
              <span className="text-xs font-normal text-slate-900">{priorityInfo.name}</span>
            </LabeledField>

            <LabeledField label={t('components.ticket:fields.status')}>
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: statusColor }}
                />
                <span className="text-xs font-normal text-slate-900">{statusDisplayName}</span>
              </div>
            </LabeledField>
          </div>

          {/* Divider */}
          <div className="h-px bg-black my-3" />

          {/* Gewerk und Typ - Side by Side */}
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label={t('components.ticket:fields.gewerk')}>
              <span className="text-xs font-normal text-slate-900">{gewerkDisplayName}</span>
            </LabeledField>

            <LabeledField label={t('components.ticket:fields.type')}>
              <span className="text-xs font-normal text-slate-900">{typeDisplayName}</span>
            </LabeledField>
          </div>

          {/* Divider */}
          <div className="h-px bg-black my-3" />

          {/* Ort und Zugewiesen an - Side by Side */}
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label={t('components.ticket:fields.location')}>
              <span className="text-xs font-normal text-slate-900">
                {ticket.location?.trim() || '-'}
              </span>
            </LabeledField>

            <LabeledField label={t('components.ticket:fields.assignedTo')}>
              <span className="text-xs font-normal text-slate-900">{assignedName}</span>
            </LabeledField>
          </div>

          {/* Divider */}
          <div className="h-px bg-black my-3" />

          {/* Fällig bis und Mesai - Side by Side (if dueDate exists) */}
          {ticket.dueDate && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <LabeledField label={t('components.ticket:fields.dueDate')}>
                  <span className="text-xs font-normal text-slate-900">{formatDate(ticket.dueDate)}</span>
                </LabeledField>

                <LabeledField label={t('components.ticket:overtime.label')}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-normal text-slate-900">
                      {overtimeNeeded ? `${overtimeHours || 0} ${t('components.ticket:overtime.hours')}` : '-'}
                    </span>
                    {overtimeNeeded && (
                      <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    )}
                  </div>
                </LabeledField>
              </div>
            </>
          )}
        </div>

        {/* Right Side Icons Bar */}
        <div className="w-12 flex flex-col items-center justify-start gap-4 py-3 pr-2">
          {/* Likes (Heart) */}
          <button
            onClick={handleLike}
            disabled={isLikeInProgress || isPublicView}
            className="flex flex-col items-center p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={userHasLiked ? t('components.ticket:likes.unlike') : t('components.ticket:likes.like')}
          >
            {isLikeInProgress ? (
              <LogoLoader size="sm" />
            ) : (
              <img 
                src={userHasLiked ? IconHeartFilled : IconHeartOutline} 
                alt="Like" 
                className="h-6 w-6" 
              />
            )}
            <span className="text-xs text-gray-600 mt-0.5">
              {localLikeCount !== undefined ? localLikeCount : (ticket.likes || 0)}
            </span>
          </button>

          {/* Comments (Chat) with count in gray badge */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsChatDialogOpen(true);
            }}
            disabled={isPublicView}
            className="flex flex-col items-center p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('components.ticket:fields.history.chatTitle')}
          >
            <img src={IconMessage} alt="Comments" className="h-6 w-6" />
            <div className="bg-gray-200 rounded px-1.5 py-0.5 mt-0.5">
              <span className="text-xs text-gray-700 font-medium">{commentsCount}</span>
            </div>
          </button>

          {/* Bookmark */}
          <button
            onClick={handleBookmark}
            disabled={isBookmarkInProgress || isPublicView}
            className="p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={isBookmarked ? t('components.ticket:bookmark.remove') : t('components.ticket:bookmark.add')}
          >
            {isBookmarkInProgress ? (
              <LogoLoader size="sm" />
            ) : (
              <img 
                src={isBookmarked ? IconBookmarkFilled : IconBookmark} 
                alt="Bookmark" 
                className="h-6 w-6" 
              />
            )}
          </button>

          {/* List (open ticket form) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenTicket) {
                onOpenTicket();
              } else {
                setIsTicketModalOpen(true);
              }
            }}
            disabled={isPublicView}
            className="p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('components.ticket:actions.edit')}
          >
            <img src={IconSquareList} alt="Open Ticket" className="h-6 w-6" />
          </button>

          {/* Share/Upload */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExportModalOpen(true);
            }}
            disabled={isExporting || isPublicView}
            className="p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('components.ticket:actions.export')}
          >
            {isExporting ? (
              <LogoLoader size="sm" />
            ) : (
              <img src={IconUpload} alt="Share" className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Chat Dialog */}
      <Dialog open={isChatDialogOpen} onOpenChange={setIsChatDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('components.ticket:fields.history.chatTitle')} - #{ticket.ticketNumber}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <TicketChat ticketId={ticket.id} className="h-[400px]" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Edit Modal */}
      <EditTicketInlineModal
        isOpen={isTicketModalOpen}
        onClose={handleTicketModalClose}
        ticket={ticket}
        projectId={ticket.projectId}
        projectName={project?.name}
        planLocation={(ticket as any).planLocation}
        onUpdated={() => {
          onTicketUpdated?.();
          queryClient.invalidateQueries({ queryKey: ['/api/tickets'] });
        }}
      />

      {/* Export Options Modal */}
      <TicketExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExportPDF}
      />
    </div>
  );
}
